import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Layout from "../components/Layout";
import Card from "../components/Card";
import Alert from "../components/Alert";
import Button from "../components/Button";

const ProductDetails = () => {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [uploader, setUploader] = useState(null);
  const [personalInfo, setPersonalInfo] = useState(null);
  const [address, setAddress] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const API_BASE_URL = 'https://api.tenarx.net';
  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      setError(null);
      try {
        const token = localStorage.getItem("userToken");
        // Fetch product details
        const res = await fetch(`${API_BASE_URL}/api/products/${productId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (!res.ok || !data.success) throw new Error(data.message || "Failed to fetch product");
        setProduct(data.data);
        // Fetch uploader details
        const uploaderId = data.data.pharmacyId || data.data.supplierId;
        if (uploaderId) {
          const userRes = await fetch(`${API_BASE_URL}/api/auth/user/${uploaderId}`);
          const userData = await userRes.json();
          console.log(userData);
          if (userRes.ok && userData.success) {
            setUploader(userData.user);
            if (userData.personalInfo) {
              setPersonalInfo(userData.personalInfo);
              console.log(userData.personalInfo);
              // If lat/lon present, fetch address
              if (userData.personalInfo.latitude && userData.personalInfo.longitude) {
                const lat = userData.personalInfo.latitude;
                const lon = userData.personalInfo.longitude;
                try {
                  const geoRes = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lon}`);
                  const geoData = await geoRes.json();
                  setAddress(geoData.display_name || "");
                } catch {
                  setAddress("");
                }
              }
            }
          }
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDetails();
  }, [productId]);

  // Helper for company name
  const getCompanyName = () => {
    return personalInfo?.company || uploader?.company || uploader?.email;
  };

  // Helper for location
  const getLocation = () => {
    if (address) {
      return (
        <a
          href={`https://www.openstreetmap.org/?mlat=${personalInfo.latitude}&mlon=${personalInfo.longitude}#map=18/${personalInfo.latitude}/${personalInfo.longitude}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 underline"
        >
          {address}
        </a>
      );
    }
    return personalInfo?.country || uploader?.country || "-";
    
    
  };

  return (
    <Layout title="Product Details">
      {loading ? (
        <div className="text-center py-12">Loading...</div>
      ) : error ? (
        <Alert type="error" message={error} />
      ) : !product ? (
        <div className="text-center py-12 text-gray-500">Product not found.</div>
      ) : (
        <div className="max-w-5xl mx-auto py-10 px-4 md:px-0">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Product Image & Info */}
            <div className="flex-1 flex flex-col gap-6">
              <Card className="shadow-lg p-0 overflow-hidden">
                <div className="bg-gradient-to-br from-blue-50 to-blue-100 flex items-center justify-center h-72 md:h-96 overflow-hidden">
                  {product.image ? (
                    <img
                      src={
                        product.image.startsWith('http')
                          ? product.image
                          : product.image.startsWith('/uploads/')
                            ? `${API_BASE_URL}${product.image}`
                            : `${API_BASE_URL}/uploads/products/${product.image}`
                      }
                      alt={product.name}
                      className="object-contain h-full w-full max-h-80 max-w-full"
                      onError={(e) => {
                        console.error('Image failed to load:', product.image);
                        e.currentTarget.style.display = 'none';
                        const placeholder = document.createElement('span');
                        placeholder.className = 'text-gray-400';
                        placeholder.textContent = 'Image not available';
                        e.currentTarget.parentNode.appendChild(placeholder);
                      }}
                    />
                  ) : (
                    <span className="text-gray-400">No image</span>
                  )}
                </div>
                <div className="p-6">
                  <h2 className="text-2xl font-bold mb-2 text-gray-900">{product.name}</h2>
                  <div className="mb-2 text-xs inline-block px-3 py-1 rounded-full bg-blue-100 text-blue-700 font-semibold">{product.category}</div>
                  <div className="text-blue-600 font-bold text-2xl mb-4">ETB {product.price}</div>
                  <div className="mb-2 text-gray-700"><span className="font-semibold">Information:</span> {product.information || product.content}</div>
                  <div className="mb-2 text-gray-700"><span className="font-semibold">Type:</span> {product.type || "-"}</div>
                  <div className="mb-2 text-gray-700"><span className="font-semibold">Country:</span> {product.country || "-"}</div>
                  {product.expireDate && (
                    <div className="mb-2 text-gray-700"><span className="font-semibold">Expire Date:</span> {new Date(product.expireDate).toLocaleDateString()}</div>
                  )}
                </div>
              </Card>
            </div>
            {/* Uploader Info */}
            <div className="w-full md:w-80 flex-shrink-0">
              <Card className="shadow-lg p-6 bg-gradient-to-br from-gray-50 to-gray-100 border border-blue-100">
                <h3 className="text-lg font-bold mb-4 text-blue-700 flex items-center gap-2">
                  <svg className="w-5 h-5 text-blue-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  Uploader Details
                </h3>
                <div className="mb-2"><span className="font-semibold">Company Name:</span> {personalInfo?.company}</div>
                <div className="mb-2"><span className="font-semibold">Location:</span> {getLocation()}</div>
                <div className="mb-2"><span className="font-semibold">Rating:</span> {uploader?.rating ? uploader.rating : <span className="text-gray-400">No rating</span>}</div>
                <div className="mb-2"><span className="font-semibold">Email:</span> {uploader?.email}</div>
              </Card>
            </div>
          </div>
          <div className="mt-10 flex justify-center">
            <Button variant="secondary" className="px-8 py-2 text-lg rounded shadow" onClick={() => navigate(-1)}>
              &larr; Back
            </Button>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProductDetails; 